import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Spinner } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import clsx from 'clsx'

import { PageTitle } from '../../../_metronic/layout/core'
import { EMatchingPayoutTicketStatus, EMatchingStatus } from '../../../types/enums'
import { formatSum } from '../../../utils'
import { useAuth } from '../auth'
import { getMatchingPayoutTickets } from '../matching/core/_requests'
import { getVisibleActivePayoutColumns, MatchingPayoutTicket } from '../matching/core/_models'
import { formatMatchingTimeout } from '../matching/core/matchingTimeout'
import { formatMskDateTime } from '../matching/routes/Dashboard/core/mskTime'
import { matchingActivePayoutsConfig } from '../matching/components/payouts-active/config'
import { matchingFinishedPayoutsConfig } from '../matching/components/payouts-finished/config'
import { PayoutRequisites } from '../matching/components/common/PayoutRequisites'
import { PayoutRowActions } from '../matching/components/common/PayoutRowActions'
import { PayoutDepositsTable } from '../matching/components/common/PayoutDepositsTable'
import {
  usePayoutTicketActions,
  usePayoutTicketPermissions,
} from '../matching/components/common/usePayoutTicketActions'
import { BORDER, FACT_GRID, INK, MUTED, NO_BREADCRUMBS } from './core/consts'
import { Fact } from './components/Fact'
import PayoutMatchingHistory from './components/PayoutMatchingHistory'

/**
 * Every status a matching payout can be listed with: the card opens an active payout and a finished
 * one alike, and the list endpoint requires the status filter.
 */
const PAYOUT_STATUSES = [
  ...matchingActivePayoutsConfig.relevantStatusList,
  ...matchingFinishedPayoutsConfig.relevantStatusList,
]

const TIMEOUT_TICK_MS = 30_000

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div
    className='d-flex align-items-center'
    style={{ minHeight: 56, padding: '0 28px', borderBottom: `1px solid ${BORDER}` }}
  >
    <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: INK }}>{title}</h2>
  </div>
)

const formatRanges = (ranges: MatchingPayoutTicket['depositRange']): string => {
  if (!ranges?.length) return '—'

  const bound = (v: number | null | undefined, round: (n: number) => number) =>
    v === null || v === undefined ? '?' : formatSum(round(v))

  return (ranges as [number | null | undefined, number | null | undefined][])
    .map(([min, max]) => `${bound(min, Math.ceil)} - ${bound(max, Math.floor)}`)
    .join(', ')
}

/**
 * A matching payout as the matching section sees it: its facts, its matched deposits and the same
 * actions as its row in the active payouts list.
 *
 * The data is that row: the list endpoint is asked for this one token, so the card shows exactly
 * what the list shows and is scoped by the same merchant rules.
 */
const MatchingPayoutCardPage: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const intl = useIntl()
  const { currentUser } = useAuth()
  const { canManageTickets, canCreateVirtualPayment, canResend } =
    usePayoutTicketPermissions(currentUser)

  const [payout, setPayout] = useState<MatchingPayoutTicket | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [nowMs, setNowMs] = useState<number>(() => Date.now())
  // The card owns the "overpayment already created" lock, as the list row does: it must survive a
  // reload of the card, which re-renders the deposits table.
  const overpaymentLock = useRef<Set<number>>(new Set())

  const reload = useCallback(() => setReloadKey((n) => n + 1), [])
  const payoutActions = usePayoutTicketActions({ enabled: canManageTickets, onDone: reload })

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), TIMEOUT_TICK_MS)

    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    if (!token) return

    let cancelled = false
    setError(null)

    const load = async () => {
      try {
        const { data } = await getMatchingPayoutTickets({
          page: 1,
          perPage: 10,
          search: token,
          statusList: PAYOUT_STATUSES,
        })

        if (cancelled) return

        if (!data?.success) {
          setError('Не удалось загрузить выплату')

          return
        }

        // A UUID search also matches a merchant transaction id equal to it: only the row of this
        // very payout is the card's subject.
        const row = (data.list ?? []).find((t) => t.ticketToken === token)
        if (!row) {
          setPayout(null)
          setError('Выплата не найдена')

          return
        }

        setPayout(row)
      } catch {
        if (!cancelled) setError('Не удалось загрузить выплату')
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [token, reloadKey])

  const isTerminal =
    payout?.status === EMatchingPayoutTicketStatus.COMPLETED ||
    payout?.status === EMatchingPayoutTicketStatus.DECLINED
  const timeout =
    payout?.matchingStatus === EMatchingStatus.PROCESSING
      ? formatMatchingTimeout(payout.matchingTimeoutAt, nowMs)
      : null

  return (
    <>
      <PageTitle breadcrumbs={NO_BREADCRUMBS}>Выплата мэтчинга</PageTitle>
      {payoutActions.modals}

      <div className='d-flex align-items-center' style={{ gap: 12, marginBottom: 18 }}>
        <span className='badge badge-light-info'>Сервис мэтчинга</span>
        <div style={{ flexGrow: 1 }} />
        {/* A finished payout is no longer managed, but its callback can still be resent — as in
            the finished payouts list. */}
        {payout && ((canManageTickets && !isTerminal) || canResend) && (
          <PayoutRowActions
            ticket={payout}
            canManageTickets={canManageTickets && !isTerminal}
            canCreateVirtualPayment={canCreateVirtualPayment}
            canResend={canResend}
            virtualPaymentState={payoutActions.virtualPaymentState(payout.ticketToken)}
            onPartialComplete={payoutActions.openComplete}
            onCancel={payoutActions.openCancel}
            onRequisiteReplaced={reload}
          />
        )}
      </div>

      {error && (
        <div className='card' style={{ padding: '24px 28px' }}>
          <div style={{ fontSize: 14, color: MUTED }}>{error}</div>
        </div>
      )}

      {!error && !payout && (
        <div className='d-flex justify-content-center' style={{ padding: 48 }}>
          <Spinner animation='border' variant='primary' />
        </div>
      )}

      {!error && payout && (
        <>
          <div className='card' style={{ marginBottom: 18 }}>
            <SectionHeader title='Выплата' />
            <div style={FACT_GRID}>
              <Fact label='Токен' value={payout.ticketToken} />
              <Fact
                label='Статус'
                value={intl.formatMessage({ id: 'ticket.status.' + payout.status })}
              />
              <Fact
                label='Статус мэтчинга'
                value={
                  payout.matchingStatus ? (
                    <span className='d-inline-flex align-items-center' style={{ gap: 6 }}>
                      {intl.formatMessage({ id: 'matching.status.' + payout.matchingStatus })}
                      {timeout && (
                        <span className={clsx('badge', timeout.danger ? 'badge-danger' : 'badge-light')}>
                          {timeout.text}
                        </span>
                      )}
                    </span>
                  ) : (
                    '—'
                  )
                }
              />
              <Fact label='Сумма' value={payout.amount === null ? '—' : formatSum(payout.amount)} />
              <Fact label='Остаток мэтчинга' value={formatSum(payout.matchingRemainder)} />
              <Fact label='Матчей' value={String(payout.liveMatchCount ?? payout.matchCount)} />
              <Fact label='Варианты депозитов' value={formatRanges(payout.depositRange)} />
              <Fact label='Мерчант' value={payout.merchant || '—'} />
              <Fact label='ID тикета мерчанта' value={payout.merchantTransactionId || '—'} />
              <Fact label='Клиент' value={payout.externalUserId || '—'} />
              <Fact label='Метод' value={payout.paymentMethod || '—'} />
              <Fact
                label='Реквизиты'
                value={
                  <PayoutRequisites
                    requisites={payout.requisites}
                    fallback={{
                      identifier: payout.matchingTicketRequisite,
                      bank: payout.matchingTicketBank,
                      cardholder: payout.matchingTicketCardholder,
                    }}
                  />
                }
              />
              <Fact label='Создана' value={formatMskDateTime(payout.createdAt)} />
              <Fact
                label='Завершена'
                value={payout.completionDate ? formatMskDateTime(payout.completionDate) : '—'}
              />
            </div>
          </div>

          <div className='card' style={{ marginBottom: 18 }}>
            <SectionHeader title='Сматченные депозиты' />
            {payout.paymentTicketList?.length ? (
              <PayoutDepositsTable
                ticket={payout}
                visibility={getVisibleActivePayoutColumns(currentUser)}
                onOverpaymentCreated={reload}
                onOverpaymentHandedToCassa={reload}
                overpaymentLock={overpaymentLock}
              />
            ) : (
              <div style={{ padding: '18px 28px', fontSize: 13, color: MUTED }}>Депозитов нет</div>
            )}
          </div>

          <div className='card'>
            <SectionHeader title='История' />
            <PayoutMatchingHistory token={payout.ticketToken} reloadKey={reloadKey} />
          </div>
        </>
      )}
    </>
  )
}

export default MatchingPayoutCardPage
